/* Screen 1 - Enhance Your Coverage */

import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getHomeCoverPrices } from "../../../../api/quoteServices";
import { processHomeCPR, transformCPRData } from "../../../../api/cprServices";
import { policyPremium } from "../../../../redux/features/motor/renewalPremiumSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import erroricon from "../../../../assets/images/error.svg";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { currencyFormatter } from "../../../../utils";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import Input from "../../../../components/common/Input/Input";
import FileUploadInput from "../../../../components/common/newFileUploadInput/FileUploadInput";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../components/common/SingleDatePicker/SingleDatePicker";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import RenewalLayout from "../../components/RenewalLayout";
import FormTabs from "../../../../components/common/tabs/formTabs";
import { CoversListSkeleton } from "../components/HomeRenewalSkeleton";

// Cover code mapping
const COVER_CODES = {
  ALL_RISK: "CPRO21",
  DOMESTIC_HELPER: "CPRO35",
  VIP: "CPRO13",
  TERRORISM: "CPRO33",
};

const EnhanceCoveragePage = ({
  policyDetailsInfo,
  onContinue,
  onBack,
  formData,
  setFormData,
  premiumData,
  setPremiumData,
  premiumErrorFromParent,
  premiumLoadingFromParent,
}) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  // Cover prices state
  const [coverPrices, setCoverPrices] = useState([]);
  const [coverPricesLoading, setCoverPricesLoading] = useState(true);

  // Premium state
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumError, setPremiumError] = useState(null);

  // Error state
  const [errors, setErrors] = useState({});

  // Calculate total premium (use premiumData if available, fallback to frontend calculation)
  const calculateTotalPremium = () => {
    // Use premium API data if available
    if (premiumData?.TotalPremium) {
      return premiumData.TotalPremium;
    }

    // Fallback to frontend calculation
    const basePremium =
      parseFloat(policyDetailsInfo?.Premium || 0) +
      parseFloat(policyDetailsInfo?.Tax || 0);

    // Add cover prices for newly selected covers (not original)
    let additionalPremium = 0;

    if (formData.allRisk && !formData.originalCovers?.allRisk) {
      additionalPremium += getCoverPriceByCode(COVER_CODES.ALL_RISK);
    }
    if (formData.vipCover && !formData.originalCovers?.vipCover) {
      additionalPremium += getCoverPriceByCode(COVER_CODES.VIP);
    }
    if (formData.terrorism && !formData.originalCovers?.terrorism) {
      additionalPremium += getCoverPriceByCode(COVER_CODES.TERRORISM);
    }
    if (formData.domesticHelper && !formData.originalCovers?.domesticHelper) {
      additionalPremium += getCoverPriceByCode(COVER_CODES.DOMESTIC_HELPER);
    }

    // Add tax on additional premium (10%)
    const additionalTax = additionalPremium * 0.1;

    return basePremium + additionalPremium + additionalTax;
  };

  // Helper function to translate cover names
  const translateCoverName = (coverName) => {
    const coverTranslations = {
      "All Risk": translate("All Risk", "جميع المخاطر"),
      VIP: translate("VIP Cover", "تغطية كبار الشخصيات"),
      "Domestic Helper": translate("Domestic Helper", "العامل المنزلي"),
      Terrorism: translate(
        "Sabotage and Terrorism Cover",
        "تغطية التخريب والإرهاب"
      ),
      "Sabotage and Terrorism": translate(
        "Sabotage and Terrorism Cover",
        "تغطية التخريب والإرهاب"
      ),
    };

    return coverTranslations[coverName] || coverName;
  };

  // Helper function to get cover price by code
  const getCoverPriceByCode = (coverCode) => {
    if (!coverPrices || !Array.isArray(coverPrices)) return 0;
    const cover = coverPrices.find((c) => c.CoverCode === coverCode);
    return cover?.Price || 0;
  };

  // Fetch cover prices from API
  const fetchCoverPrices = async () => {
    if (!policyDetailsInfo) return;

    setCoverPricesLoading(true);

    try {
      // Build home form data for cover prices API.
      // PolicyHeader uses FixValue (building), ContentValue (contents), PersonalValue.
      // SumInsured is passed as a direct fallback for when the component values are zero.
      const homeFormData = {
        homeDetails: {
          buildingValue: policyDetailsInfo?.FixValue || policyDetailsInfo?.BuildingValue || 0,
          contentsValue: policyDetailsInfo?.ContentValue || policyDetailsInfo?.ContentsValue || 0,
          personalBelongingsValue: policyDetailsInfo?.PersonalValue || policyDetailsInfo?.PersonalBelongingsValue || 0,
          sumInsured: policyDetailsInfo?.SumInsured || 0,
        },
      };

      const result = await getHomeCoverPrices(homeFormData, { timeout: 15000 });

      if (result.success && result.covers) {
        setCoverPrices(result.covers);

        // Store in form data
        setFormData((prev) => ({
          ...prev,
          coverPrices: result.covers,
        }));
      } else {
        console.error("Failed to fetch cover prices:", result.error);
        toast.error(
          translate(
            "Failed to load cover prices. Default pricing will be used.",
            "فشل في تحميل أسعار التغطية. سيتم استخدام التسعيرة الافتراضية."
          )
        );
      }
    } catch (error) {
      console.error("Cover prices fetch error:", error);
      toast.error(
        translate(
          "Error loading cover prices.",
          "خطأ في تحميل أسعار التغطية."
        )
      );
    } finally {
      setCoverPricesLoading(false);
    }
  };

  // Fetch cover prices on mount
  useEffect(() => {
    if (policyDetailsInfo) {
      fetchCoverPrices();
    }
  }, [policyDetailsInfo]);

  // Helper function to check if domestic helper fields are complete and valid
  const isDomesticHelperFormComplete = () => {
    if (!formData.domesticHelper || formData.originalCovers?.domesticHelper) {
      return false;
    }

    // Check if all required fields are filled
    const hasFullName = formData.domesticHelperFullName && formData.domesticHelperFullName.trim() !== "";
    const hasValidName = hasFullName && /^[a-zA-Z\u0600-\u06FF\s.]+$/.test(formData.domesticHelperFullName);
    const hasCPR = formData.cprFiles && formData.cprFiles.length > 0;
    const hasCprNumber = formData.domesticHelperCpr && formData.domesticHelperCpr.trim() !== "";
    const hasDateOfBirth =
      formData.domesticHelperDateOfBirth?.day &&
      formData.domesticHelperDateOfBirth?.month &&
      formData.domesticHelperDateOfBirth?.year;

    // Check if date of birth is valid (18+ years old, not in future)
    let isValidAge = false;
    if (hasDateOfBirth) {
      const birthDate = new Date(
        parseInt(formData.domesticHelperDateOfBirth.year),
        parseInt(formData.domesticHelperDateOfBirth.month) - 1,
        parseInt(formData.domesticHelperDateOfBirth.day)
      );
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      isValidAge = age >= 18 && birthDate <= today;
    }

    return hasValidName && hasCPR && hasCprNumber && hasDateOfBirth && isValidAge;
  };

  // Debounced premium API call for domestic helper
  useEffect(() => {
    // Only proceed if domestic helper is newly selected and form is complete
    if (!isDomesticHelperFormComplete()) {
      return;
    }

    // Don't call if CPR is processing
    if (formData.cprProcessing) {
      return;
    }

    // Don't call if premium is already loading
    if (premiumLoading) {
      return;
    }

    console.log("✅ Domestic helper form complete, scheduling premium API call...");

    // Debounce timer (500ms)
    const timer = setTimeout(() => {
      console.log("✅ Calling premium API for domestic helper...");
      fetchPremiumForCovers();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    formData.domesticHelper,
    formData.domesticHelperFullName,
    formData.domesticHelperDateOfBirth,
    formData.cprFiles,
    formData.cprProcessing,
  ]);

  // Build premium API payload (accepts optional formData override)
  const buildPremiumPayload = (overrideFormData = null) => {
    const currentFormData = overrideFormData || formData;

    const payload = {
      PolicyNumber: policyDetailsInfo?.PolicyNumber,
      Channel: getClientSource(),
    };

    // Check if any cover changed from original
    const hasChanges =
      currentFormData.vipCover !== currentFormData.originalCovers?.vipCover ||
      currentFormData.allRisk !== currentFormData.originalCovers?.allRisk ||
      currentFormData.terrorism !== currentFormData.originalCovers?.terrorism ||
      currentFormData.domesticHelper !== currentFormData.originalCovers?.domesticHelper;

    if (!hasChanges) {
      // No changes - keep defaults
      payload.DefaultCovers = true;
    } else {
      // Changes detected
      payload.DefaultCovers = false;

      // Add covers if selected and not original
      if (currentFormData.vipCover && !currentFormData.originalCovers?.vipCover) {
        payload.VIP = true;
      }
      if (currentFormData.allRisk && !currentFormData.originalCovers?.allRisk) {
        payload.AllRisk = true;
        payload.PersonalValue = policyDetailsInfo?.PersonalValue || policyDetailsInfo?.PersonalBelongingsValue || 0;
      }
      if (currentFormData.terrorism && !currentFormData.originalCovers?.terrorism) {
        payload.Terrorism = true;
      }
      if (currentFormData.domesticHelper && !currentFormData.originalCovers?.domesticHelper) {
        payload.DomesticHelper = true;
        payload.DomesticHelperCount = 1;
        payload.DomesticHelperLimit = parseInt((currentFormData.domesticHelperSumInsured || "500").replace(/,/g, ""), 10);
        payload.DomesticMembers = [
          {
            Name: currentFormData.domesticHelperFullName || "",
            CprOrPassport: currentFormData.domesticHelperCpr || "",
          },
        ];
      }
    }

    return payload;
  };

  // Fetch premium for selected covers (accepts optional formData override)
  const fetchPremiumForCovers = async (overrideFormData = null) => {
    setPremiumLoading(true);
    setPremiumError(null);

    try {
      const payload = buildPremiumPayload(overrideFormData);
      const currentFormData = overrideFormData || formData;

      console.log("✅ Calling premium API for covers:", payload);

      const premiumAction = await dispatch(policyPremium(payload));
      const premiumResponse = unwrapResult(premiumAction);

      setPremiumData(premiumResponse);
      console.log("✅ Premium calculated:", premiumResponse);

      // Reconcile covers: if a newly-selected cover is absent from the
      // quote response, unselect it and inform the user.
      // Only relevant when we sent cover additions (DefaultCovers = false).
      if (!payload.DefaultCovers) {
        const qd = premiumResponse?.QuoteDetails || {};
        // Support both boolean-flag format and Covers-array format
        const responseCoverCodes = (qd.Covers || []).map((c) => c.CoverCode);
        const isCoverInResponse = (boolField, coverCode) =>
          qd[boolField] === true || responseCoverCodes.includes(coverCode);

        const reconciled = { ...currentFormData };
        const droppedEn = [];
        const droppedAr = [];

        if (reconciled.allRisk && !reconciled.originalCovers?.allRisk && !isCoverInResponse("AllRisk", "CPRO21")) {
          reconciled.allRisk = false;
          droppedEn.push("All Risk");
          droppedAr.push("جميع المخاطر");
        }
        if (reconciled.vipCover && !reconciled.originalCovers?.vipCover && !isCoverInResponse("VIP", "CPRO13")) {
          reconciled.vipCover = false;
          droppedEn.push("VIP Cover");
          droppedAr.push("تغطية كبار الشخصيات");
        }
        if (reconciled.terrorism && !reconciled.originalCovers?.terrorism && !isCoverInResponse("Terrorism", "CPRO33")) {
          reconciled.terrorism = false;
          droppedEn.push("Sabotage and Terrorism Cover");
          droppedAr.push("تغطية التخريب والإرهاب");
        }
        if (reconciled.domesticHelper && !reconciled.originalCovers?.domesticHelper && !isCoverInResponse("DomesticHelper", "CPRO35")) {
          reconciled.domesticHelper = false;
          droppedEn.push("Domestic Helper");
          droppedAr.push("العامل المنزلي");
        }

        if (droppedEn.length > 0) {
          setFormData(reconciled);
          toast.warning(
            translate(
              `The following cover(s) were not accepted by the policy and have been removed: ${droppedEn.join(", ")}`,
              `التغطية(التغطيات) التالية لم يتم قبولها في الوثيقة وتمت إزالتها: ${droppedAr.join("، ")}`
            )
          );
        }
      }

    } catch (error) {
      console.error("❌ Premium API error:", error);
      setPremiumError(error.message);
      // NOTE: Don't show toast here - parent component handles error display
      // This prevents duplicate error toasts
    } finally {
      setPremiumLoading(false);
    }
  };

  // Handle switch change for covers
  const handleSwitchChange = (coverKey, value) => {
    // Create updated formData with the new value
    const updatedFormData = {
      ...formData,
      [coverKey]: value,
    };

    // Update state
    setFormData(updatedFormData);

    // Clear errors when switching off domestic helper
    if (coverKey === "domesticHelper" && !value) {
      setErrors((prev) => ({
        ...prev,
        domesticHelperFullName: "",
        domesticHelperDateOfBirth: "",
        cprUpload: "",
      }));
    }

    // Call premium API with updated formData (non-domestic helper covers)
    if (coverKey !== "domesticHelper") {
      fetchPremiumForCovers(updatedFormData);
    }
  };

  // Handle date change for domestic helper
  const handleDateChange = (field, value) => {
    setFormData((prev) => {
      const currentDOB = prev.domesticHelperDateOfBirth || {
        day: "",
        month: "",
        year: "",
        fullDate: "",
      };

      const newDOB = {
        ...currentDOB,
        [field]: value,
      };

      // Calculate fullDate if all fields are present
      if (newDOB.day && newDOB.month && newDOB.year) {
        newDOB.fullDate = `${newDOB.year}-${newDOB.month.padStart(
          2,
          "0"
        )}-${newDOB.day.padStart(2, "0")}`;
      } else {
        newDOB.fullDate = "";
      }

      return {
        ...prev,
        domesticHelperDateOfBirth: newDOB,
      };
    });

    // Immediate DOB validation once all 3 fields are filled
    const updatedDOB = { ...formData.domesticHelperDateOfBirth, [field]: value };
    if (updatedDOB.day && updatedDOB.month && updatedDOB.year) {
      const birthDate = new Date(
        parseInt(updatedDOB.year),
        parseInt(updatedDOB.month) - 1,
        parseInt(updatedDOB.day)
      );
      const today = new Date();
      if (birthDate > today) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperDateOfBirth: translate(
            "Date of birth cannot be in the future.",
            "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
          ),
        }));
      } else {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) {
          setErrors((prev) => ({
            ...prev,
            domesticHelperDateOfBirth: translate(
              "Helper must be at least 18 years old.",
              "يجب أن يكون عمر العامل 18 سنة على الأقل."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, domesticHelperDateOfBirth: "" }));
        }
      }
    } else {
      if (errors.domesticHelperDateOfBirth) {
        setErrors((prev) => ({ ...prev, domesticHelperDateOfBirth: "" }));
      }
    }
  };

  // Handle date picker selection (for SingleDatePicker component)
  const handleDatePickerSelect = (selectedDate) => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();

      setFormData((prev) => ({
        ...prev,
        domesticHelperDateOfBirth: {
          day,
          month,
          year,
          fullDate: selectedDate,
        },
      }));

      // Immediate DOB validation
      const today = new Date();
      const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (birthDate > today) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperDateOfBirth: translate(
            "Date of birth cannot be in the future.",
            "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
          ),
        }));
      } else {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) {
          setErrors((prev) => ({
            ...prev,
            domesticHelperDateOfBirth: translate(
              "Helper must be at least 18 years old.",
              "يجب أن يكون عمر العامل 18 سنة على الأقل."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, domesticHelperDateOfBirth: "" }));
        }
      }
    }
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Immediate inline validation for full name
    if (field === "domesticHelperFullName") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperFullName: translate(
            "This field is required.",
            "هذا الحقل مطلوب"
          ),
        }));
      } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperFullName: translate(
            "Name can only contain alphabetic characters, spaces, and periods.",
            "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
          ),
        }));
      } else {
        setErrors((prev) => ({ ...prev, domesticHelperFullName: "" }));
      }
      return;
    }

    // Inline validation for CPR number
    if (field === "domesticHelperCpr") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperCpr: translate(
            "This field is required.",
            "هذا الحقل مطلوب"
          ),
        }));
      } else {
        setErrors((prev) => ({ ...prev, domesticHelperCpr: "" }));
      }
      return;
    }

    // Clear error when user starts typing for other fields
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // CPR file upload handler
  const handleFileSelect = async (fileData) => {
    // Strict validation: Only allow maximum 2 files for CPR (front and back)
    const currentFiles = formData.cprFiles || [];
    if (currentFiles.length >= 2) {
      toast.error(
        translate(
          "Maximum 2 files allowed (CPR front and back)",
          "يُسمح بحد أقصى ملفين (الوجه الأمامي والخلفي للهوية)"
        )
      );
      return;
    }

    // Update form with uploaded file
    setFormData((prev) => {
      const existingFiles = prev.cprFiles || [];
      if (existingFiles.length >= 2) {
        return prev;
      }
      return {
        ...prev,
        cprFiles: [...existingFiles, fileData],
        cprProcessing: true,
      };
    });

    // Clear error
    setErrors((prev) => ({
      ...prev,
      cprUpload: "",
    }));

    // Process CPR with Vista OCR in background
    if (fileData && fileData.file) {
      try {
        const currentFileCount = formData.cprFiles?.length || 0;
        const side = currentFileCount === 0 ? "front" : "back";

        const result = await processHomeCPR(fileData.file, side);

        if (result.success && result.data?.output?.status) {
          const cprData = transformCPRData(result.data);

          // Auto-fill form data with CPR information
          setFormData((prev) => ({
            ...prev,
            domesticHelperFullName:
              cprData.fullName || prev.domesticHelperFullName,
            domesticHelperDateOfBirth:
              cprData.dateOfBirth &&
              cprData.dateOfBirth.day &&
              cprData.dateOfBirth.month &&
              cprData.dateOfBirth.year
                ? {
                    day: cprData.dateOfBirth.day,
                    month: cprData.dateOfBirth.month,
                    year: cprData.dateOfBirth.year,
                    fullDate: `${
                      cprData.dateOfBirth.year
                    }-${cprData.dateOfBirth.month.padStart(
                      2,
                      "0"
                    )}-${cprData.dateOfBirth.day.padStart(2, "0")}`,
                  }
                : prev.domesticHelperDateOfBirth,
            cprProcessing: false,
            cprData: cprData,
            domesticHelperCpr: cprData.cprNumber || prev.domesticHelperCpr,
          }));

          toast.success(
            translate(
              "CPR processed successfully! Helper information has been auto-filled.",
              "تم معالجة الهوية بنجاح! تم ملء معلومات العامل تلقائياً."
            )
          );
        } else {
          setFormData((prev) => ({
            ...prev,
            cprProcessing: false,
          }));

          toast.error(
            translate(
              "Failed to process CPR. Please fill the information manually.",
              "فشل في معالجة الهوية. يرجى ملء المعلومات يدوياً."
            )
          );
        }
      } catch (error) {
        console.error("CPR processing error:", error);
        setFormData((prev) => ({
          ...prev,
          cprProcessing: false,
        }));

        toast.error(
          translate(
            "Error processing CPR. Please try again.",
            "خطأ في معالجة الهوية. يرجى المحاولة مرة أخرى."
          )
        );
      }
    }
  };

  const handleFileRemove = () => {
    setFormData((prev) => ({
      ...prev,
      cprFiles: [],
      cprProcessing: false,
      cprData: null,
      domesticHelperCpr: "",
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate Domestic Helper fields if enabled
    if (formData.domesticHelper && !formData.originalCovers?.domesticHelper) {
      if (
        !formData.domesticHelperFullName ||
        formData.domesticHelperFullName.trim() === ""
      ) {
        newErrors.domesticHelperFullName = translate(
          "This field is required.",
          "هذا الحقل مطلوب"
        );
      } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(formData.domesticHelperFullName)) {
        newErrors.domesticHelperFullName = translate(
          "Name can only contain alphabetic characters, spaces, and periods.",
          "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
        );
      }

      if (
        !formData.domesticHelperDateOfBirth?.day ||
        !formData.domesticHelperDateOfBirth?.month ||
        !formData.domesticHelperDateOfBirth?.year
      ) {
        newErrors.domesticHelperDateOfBirth = translate(
          "This field is required.",
          "هذا الحقل مطلوب"
        );
      } else {
        // Validate age
        const birthDate = new Date(
          parseInt(formData.domesticHelperDateOfBirth.year),
          parseInt(formData.domesticHelperDateOfBirth.month) - 1,
          parseInt(formData.domesticHelperDateOfBirth.day)
        );
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        if (age < 18) {
          newErrors.domesticHelperDateOfBirth = translate(
            "Helper must be at least 18 years old.",
            "يجب أن يكون عمر العامل 18 سنة على الأقل."
          );
        }

        if (birthDate > today) {
          newErrors.domesticHelperDateOfBirth = translate(
            "Date of birth cannot be in the future.",
            "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
          );
        }
      }

      // Validate CPR upload
      if (!formData.cprFiles || formData.cprFiles.length === 0) {
        newErrors.cprUpload = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue
  const handleContinue = async () => {
    if (!validateForm()) {
      return;
    }

    // Call premium API before continuing (especially for domestic helper)
    if (formData.domesticHelper && !formData.originalCovers?.domesticHelper) {
      await fetchPremiumForCovers();
    }

    onContinue();
  };

  // Define covers in the order shown in the design
  const coversConfig = [
    {
      code: COVER_CODES.ALL_RISK,
      key: "allRisk",
      name: "All Risk",
      showForm: false,
    },
    {
      code: COVER_CODES.VIP,
      key: "vipCover",
      name: "VIP",
      showForm: false,
    },
    {
      code: COVER_CODES.DOMESTIC_HELPER,
      key: "domesticHelper",
      name: "Domestic Helper",
      showForm: true,
    },
    {
      code: COVER_CODES.TERRORISM,
      key: "terrorism",
      name: "Sabotage and Terrorism",
      showForm: false,
    },
  ];

  return (
    <>
      <div className="mainCarDetailsModal pt-[1%] pb-36 max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll">
        <RenewalLayout
          policyType="home"
          show_close_btn={false}
          is_hide_back_btn={false}
          backClickHandler={onBack}
          btn_onClick={handleContinue}
          btn_disabled={coverPricesLoading || formData.cprProcessing || premiumLoading || premiumLoadingFromParent}
          show_summary={premiumData && !premiumError && !premiumErrorFromParent}
          tempPrice={premiumData && !premiumError && !premiumErrorFromParent ? calculateTotalPremium() : undefined}
          btn_label={translate("Continue", "التالي")}
        >
          <div className="flex justify-center items-start min-h-[60vh]">
            <div className="w-full max-w-[600px] mx-auto px-4 max767:pb-10">
              {/* Title and Description */}
              <TitleDescription
                title={translate("Enhance Your Coverage", "تعزيز التغطية الخاصة بك")}
                description={translate(
                  "Would you like to add extra covers and benefits to your policy?",
                  "هل ترغب بإضافة تغطيات وفوائد إضافية إلى سياستك؟"
                )}
              />

              {/* Covers List */}
              {coverPricesLoading ? (
                <CoversListSkeleton />
              ) : (
                <div className="space-y-2 mt-8">
                  {coversConfig.map((coverConfig) => {
                    const isActive = formData[coverConfig.key] || false;
                    const isOriginallyIncluded =
                      formData.originalCovers?.[coverConfig.key] || false;
                    const coverPrice = getCoverPriceByCode(coverConfig.code);
                    const isDomesticHelper = coverConfig.showForm;

                    return (
                      <div
                        key={coverConfig.code}
                        className={`bg-white rounded-lg border-2 p-4 transition-all duration-300 ${
                          isActive ? "border-[#00719A]" : "border-[#DDE2E4]"
                        } ${isOriginallyIncluded ? "opacity-70" : ""}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <span className="text-base font-normal rtl:font-medium text-[#252C32] rtl:text-[#48535B] mr-2 rtl:mr-0 rtl:ml-2">
                              {translateCoverName(coverConfig.name)}
                            </span>
                            {isDomesticHelper && (
                              <button className="w-4 h-4 flex items-center justify-center hover:opacity-70 transition-opacity">
                                <img
                                  src={erroricon}
                                  alt="info"
                                  className="w-4 h-4 mx-1"
                                />
                              </button>
                            )}
                          </div>

                          {isOriginallyIncluded ? (
                            <span className="bg-[#EEF0F2] text-[#252C32] px-3 py-1 rounded-md text-sm font-medium">
                              {translate("Included in the plan", "شامل ضمن الخطة")}
                            </span>
                          ) : (
                            <label
                              className={`relative inline-flex items-center ${
                                coverPricesLoading || premiumLoading
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={isActive}
                                disabled={coverPricesLoading || premiumLoading}
                                onChange={() =>
                                  !coverPricesLoading && !premiumLoading &&
                                  handleSwitchChange(coverConfig.key, !isActive)
                                }
                              />
                              <div
                                className={`w-8 h-5 rounded-full transition-colors ${
                                  isActive ? "bg-[#00A9E7]" : "bg-gray-300"
                                }`}
                              >
                                <div
                                  className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                                    isActive
                                      ? isRTL
                                        ? "-translate-x-3.5"
                                        : "translate-x-3.5"
                                      : isRTL
                                      ? "-translate-x-[0.15rem]"
                                      : "translate-x-[0.15rem]"
                                  }`}
                                ></div>
                              </div>
                            </label>
                          )}
                        </div>

                        {/* Show price badge when inactive */}
                        {!isOriginallyIncluded && !isActive && (
                          <div className="flex justify-end">
                            <span className="bg-[#EEF0F2] text-[#252C32] px-2 py-0.5 rounded-full text-xs font-medium uppercase">
                              {isRTL
                                ? `+${currencyFormatter(coverPrice)} د.ب`
                                : `+ BHD ${currencyFormatter(coverPrice)}`}
                            </span>
                          </div>
                        )}

                        {/* Domestic Helper expanded form */}
                        {isDomesticHelper &&
                          isActive &&
                          !isOriginallyIncluded && (
                            <div className="mt-4 space-y-4">
                              {/* CPR Upload */}
                              <div>
                                <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                                  {translate(
                                    "Upload CPR (front & back)",
                                    "قم بتحميل CPR (الوجه والظهر)"
                                  )}
                                </label>
                                <FileUploadInput
                                  name="domestic_helper_cpr"
                                  onFileSelect={handleFileSelect}
                                  onFileRemove={handleFileRemove}
                                  validTypes={[
                                    "image/jpeg",
                                    "image/png",
                                    "application/pdf",
                                  ]}
                                  maxSizeKB={5000}
                                  uploadToServer={true}
                                  multiple={true}
                                  maxFiles={2}
                                  disabled={formData.cprProcessing}
                                  initialFiles={formData.cprFiles || []}
                                />
                                {errors.cprUpload && (
                                  <div className="mt-2 text-red-600 text-sm">
                                    {errors.cprUpload}
                                  </div>
                                )}

                                {/* CPR Processing Status */}
                                {formData.cprProcessing && (
                                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                      <p className="text-sm text-blue-700">
                                        {translate(
                                          "Processing CPR data...",
                                          "جاري معالجة بيانات الهوية..."
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Limit for absence of work */}
                              <FormTabs
                                label={translate(
                                  "Limit for absence of work",
                                  "حد الغياب عن العمل"
                                )}
                                tabs={[
                                  { type: "500", label_en: "BHD 500", label_ar: "500 د.ب" },
                                  { type: "1,000", label_en: "BHD 1,000", label_ar: "1,000 د.ب" },
                                  { type: "1,500", label_en: "BHD 1,500", label_ar: "1,500 د.ب" },
                                ]}
                                activeTab={formData.domesticHelperSumInsured}
                                onTabClick={(type) =>
                                  handleInputChange("domesticHelperSumInsured", type)
                                }
                              />

                              {/* Full Name */}
                              <div>
                                <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                                  {translate(
                                    "Full Name",
                                    "اسم العامل المنزلي"
                                  )}
                                </label>
                                <Input
                                  type="text"
                                  name="domesticHelperFullName"
                                  value={formData.domesticHelperFullName}
                                  handleChange={(e) =>
                                    handleInputChange(
                                      "domesticHelperFullName",
                                      e.target.value
                                    )
                                  }
                                  error={errors.domesticHelperFullName}
                                  disabled={formData.cprProcessing}
                                />
                              </div>

                              {/* CPR Number */}
                              <div>
                                <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                                  {translate(
                                    "CPR Number",
                                    "رقم الهوية"
                                  )}
                                </label>
                                <Input
                                  type="text"
                                  name="domesticHelperCpr"
                                  value={formData.domesticHelperCpr}
                                  handleChange={(e) =>
                                    handleInputChange(
                                      "domesticHelperCpr",
                                      e.target.value
                                    )
                                  }
                                  error={errors.domesticHelperCpr}
                                  disabled={formData.cprProcessing}
                                  placeholder={translate(
                                    "Enter CPR number",
                                    "أدخل رقم الهوية"
                                  )}
                                />
                              </div>

                              {/* Date of Birth */}
                              <div>
                                <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mb-[5px] block">
                                  {translate("Date of Birth", "تاريخ الولادة")}
                                </label>

                                {/* Mobile: native date picker */}
                                <div className="hidden max767:block">
                                  <SingleDatePicker
                                    value={
                                      formData.domesticHelperDateOfBirth
                                        ?.fullDate || ""
                                    }
                                    onChange={handleDatePickerSelect}
                                    placeholder={translate(
                                      "Select Date",
                                      "اختر التاريخ"
                                    )}
                                    disabled={formData.cprProcessing}
                                    error={errors.domesticHelperDateOfBirth}
                                    useNativeMobile={true}
                                  />
                                </div>

                                {/* Desktop: select dropdowns */}
                                <div className="block max767:hidden">
                                  <div className="flex gap-6 self-stretch">
                                    <SelectInput
                                      name="day"
                                      options_data={daysOptions}
                                      formData={{
                                        day:
                                          formData.domesticHelperDateOfBirth
                                            ?.day || "",
                                      }}
                                      default_placeholder={translate(
                                        "Day",
                                        "يوم"
                                      )}
                                      handleChange={(e) =>
                                        handleDateChange("day", e.target.value)
                                      }
                                      disabled={formData.cprProcessing}
                                      className="text-black"
                                      maxMenuHeight={185}
                                    />

                                    <SelectInput
                                      name="month"
                                      options_data={monthsOptions}
                                      formData={{
                                        month:
                                          formData.domesticHelperDateOfBirth
                                            ?.month || "",
                                      }}
                                      default_placeholder={translate(
                                        "Month",
                                        "شهر"
                                      )}
                                      handleChange={(e) =>
                                        handleDateChange(
                                          "month",
                                          e.target.value
                                        )
                                      }
                                      disabled={formData.cprProcessing}
                                      maxMenuHeight={185}
                                    />

                                    <SelectInput
                                      name="year"
                                      options_data={yearsOptions}
                                      formData={{
                                        year:
                                          formData.domesticHelperDateOfBirth
                                            ?.year || "",
                                      }}
                                      default_placeholder={translate(
                                        "Year",
                                        "سنة"
                                      )}
                                      handleChange={(e) =>
                                        handleDateChange("year", e.target.value)
                                      }
                                      disabled={formData.cprProcessing}
                                      maxMenuHeight={185}
                                    />
                                  </div>
                                  {errors.domesticHelperDateOfBirth && (
                                    <div className="mt-2 text-red-600 text-sm">
                                      {errors.domesticHelperDateOfBirth}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Price badge for active domestic helper */}
                              <div className="flex justify-end">
                                <span className="bg-[#00719A] text-white px-2 py-0.5 rounded-full text-xs font-medium uppercase">
                                  {isRTL
                                    ? `+${currencyFormatter(coverPrice)} د.ب`
                                    : `+ BHD ${currencyFormatter(coverPrice)}`}
                                </span>
                              </div>
                            </div>
                          )}

                        {/* Active price badge for non-domestic helper covers */}
                        {!isDomesticHelper &&
                          isActive &&
                          !isOriginallyIncluded && (
                            <div className="flex justify-end">
                              <span className="bg-[#00719A] text-white px-2 py-0.5 rounded-full text-xs font-medium uppercase">
                                {isRTL
                                  ? `+${currencyFormatter(coverPrice)} د.ب`
                                  : `+ BHD ${currencyFormatter(coverPrice)}`}
                              </span>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </RenewalLayout>
      </div>
    </>
  );
};

export default EnhanceCoveragePage;
